export interface LegalSection {
  heading: string
  body: string[]
}

export interface LegalDocument {
  title: string
  updated: string
  intro: string
  sections: LegalSection[]
}

export const privacyPolicy: LegalDocument = {
  title: 'Privacy Policy',
  updated: 'September 2026',
  intro:
    'A&J Pool Plaster, LLC respects your privacy. This page explains what information we collect when you request an estimate, how we use it, and the choices you have.',
  sections: [
    {
      heading: 'Information we collect',
      body: [
        'When you submit an estimate request, we collect only what we need to contact you and price the work: your name, phone number, email address (optional), city or ZIP code, the service you are interested in, your timeline, your preferred contact method, and any details you choose to share about your pool.',
        'You may also attach photos of your pool. Photos stay on your device until you send them: they are previewed locally in your browser and are only shared with us through the message you send.',
        'Our estimate form opens a text message or email from your own device. That means your details travel directly from your phone or computer to A&J — they are not stored on this website.',
      ],
    },
    {
      heading: 'How we use your information',
      body: [
        'We use your information for one purpose: to respond to your request, schedule a free on-site estimate, and complete the work you hire us for.',
        'We may contact you by phone call, text message, or email using the method you selected. Standard message and data rates from your carrier may apply.',
      ],
    },
    {
      heading: 'What we never do',
      body: [
        'We do not sell, rent, or trade your personal information to anyone.',
        'We do not add you to marketing lists without your permission, and we do not share your contact details with other contractors or lead-generation services.',
      ],
    },
    {
      heading: 'Third-party services',
      body: [
        'This site displays a map of our service area powered by OpenStreetMap and CARTO, and links to our Google Business Profile so you can read reviews. Those providers may collect standard technical information, such as your IP address, when their content loads. Their own privacy policies govern that data.',
      ],
    },
    {
      heading: 'Retention and your choices',
      body: [
        'We keep estimate requests and project records only as long as needed to serve you and to meet our licensing, insurance, and tax obligations.',
        'You can ask us to delete your contact information at any time, or ask us to stop contacting you, by calling or texting (832) 920-0133 or emailing AJPoolplaster@gmail.com.',
      ],
    },
    {
      heading: 'Questions',
      body: [
        'If you have any questions about this policy, call or text (832) 920-0133 or email AJPoolplaster@gmail.com. We will answer honestly — the same way we quote work.',
      ],
    },
  ],
}

export const termsOfService: LegalDocument = {
  title: 'Terms of Service',
  updated: 'September 2026',
  intro:
    'These terms cover the use of this website and the estimates we provide. They are not a construction contract — the signed proposal for your project always governs the work itself.',
  sections: [
    {
      heading: 'About this website',
      body: [
        'This site describes services offered by A&J Pool Plaster, LLC in the Greater Houston area. We keep the information accurate and current, but descriptions, photos, timelines, and pricing examples are for general reference only and may change without notice.',
      ],
    },
    {
      heading: 'Estimates and pricing',
      body: [
        'Estimate requests submitted here are inquiries, not contracts. No work is scheduled and no price is locked until we have visited the property and issued a written estimate that you approve.',
        'Written estimates are based on the condition of the pool as inspected. Honest pricing is our promise: if we find a hidden condition — for example structural damage revealed during chip-out — we will show you the issue, explain the options, and get your written approval before any additional cost is incurred.',
        'Any dollar figures shown on this website are illustrative ranges from past projects, not quotes for your pool.',
      ],
    },
    {
      heading: 'Scheduling and timelines',
      body: [
        'Typical plaster projects run about three to four weeks including fill and curing time. Weather, water availability, permitting, and material lead times can shift a schedule. When a date moves, we call you — we do not leave you guessing.',
        'Access to the pool area, working water, and working power at the property are your responsibility and are required for us to perform the work.',
      ],
    },
    {
      heading: 'Licensing, insurance, and workmanship',
      body: [
        'A&J Pool Plaster, LLC is licensed and insured. Proof of both is available on request before work begins.',
        'We stand behind our workmanship. Warranty coverage, exclusions, and any manufacturer warranties on materials are set out in your signed project proposal. Normal wear, chemical imbalance, improper water chemistry, neglected maintenance, and damage caused by others are not covered.',
      ],
    },
    {
      heading: 'Photos of completed work',
      body: [
        'Project photos shown on this website are pools completed by our crew. We may photograph finished work for our portfolio; tell us if you prefer your property not be photographed and we will respect that.',
      ],
    },
    {
      heading: 'Communication consent',
      body: [
        'By submitting an estimate request or texting us, you agree that we may reply using the phone number, message thread, or email address you provided. You can ask us to stop at any time.',
      ],
    },
    {
      heading: 'Contact',
      body: [
        'Questions about these terms? Call or text (832) 920-0133 or email AJPoolplaster@gmail.com.',
      ],
    },
  ],
}
